import React, {
  ButtonHTMLAttributes,
  HTMLAttributes,
  ReactNode,
  useMemo,
} from 'react';

import { sva } from '@adsk/alloy-react-helpers';
import { useTheme } from '@adsk/alloy-react-styles';
import { StyleProp } from '@adsk/alloy-react-theme';

type InlineEditContainerOwnProps = {
  /** Renders as a `<div>` when true, otherwise renders as a `<button>`. */
  editing?: boolean;
  /** Apply error styling. */
  error?: boolean;
  /** Apply disabled styling. When not editing, renders as `<div aria-disabled>` instead of `<button disabled>` so child interactive elements (e.g. links) remain clickable. */
  disabled?: boolean;
  /** Custom styles merged after the component's base styles. */
  style?: StyleProp;
  children?: ReactNode;
};

type InlineEditContainerProps = InlineEditContainerOwnProps &
  Omit<
    HTMLAttributes<HTMLElement> &
      Pick<
        ButtonHTMLAttributes<HTMLButtonElement>,
        'type' | 'name' | 'value' | 'form' | 'autoFocus'
      >,
    keyof InlineEditContainerOwnProps
  >;

// keep in sync with button-only fields of ButtonHTMLAttributes
const BUTTON_ONLY_ATTRS = new Set<string>([
  'type',
  'name',
  'value',
  'form',
  'autoFocus',
  'formAction',
  'formEncType',
  'formMethod',
  'formNoValidate',
  'formTarget',
]);

const stripButtonOnlyAttrs = (props: Record<string, unknown>) => {
  const next: Record<string, unknown> = {};
  for (const key of Object.keys(props)) {
    if (!BUTTON_ONLY_ATTRS.has(key)) {
      next[key] = props[key];
    }
  }
  return next;
};

export const InlineEditContainer = ({
  editing,
  error,
  disabled,
  style,
  children,
  ...props
}: InlineEditContainerProps) => {
  const { tokens } = useTheme();
  const containerTokens = tokens?.component.inlineEdit?.container;

  const containerStyles = useMemo(
    () =>
      sva(containerTokens?._, {
        variants: {
          disabled: containerTokens?.disabled,
          editing: containerTokens?.editing,
          error: containerTokens?.error,
        },
        compoundVariants: containerTokens?.compoundVariants,
      }),
    [containerTokens],
  );

  const css = [containerStyles({ disabled, editing, error }), style];

  if (editing) {
    return (
      <div
        css={css}
        {...(stripButtonOnlyAttrs(props) as HTMLAttributes<HTMLDivElement>)}
      >
        {children}
      </div>
    );
  }

  if (disabled) {
    return (
      <div
        role="button"
        tabIndex={0}
        css={css}
        {...(stripButtonOnlyAttrs(props) as HTMLAttributes<HTMLDivElement>)}
        aria-disabled
      >
        {children}
      </div>
    );
  }

  return (
    <button
      type="button"
      css={css}
      {...(props as ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {children}
    </button>
  );
};

InlineEditContainer.displayName = 'InlineEditContainer';
